/**
 * CreateObjectiveModal Component
 *
 * Modal for creating or editing an objective.
 * Uses React Hook Form with Zod validation for consistent form validation.
 * Follows the same pattern as CreateCycleModal.
 */

import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { ObjectiveFormSchema, ObjectiveFormData, objectiveFormDefaults } from '../../../models/objective.schemas';
import ErrorMessage from '@/components/shared/ErrorMessage/ErrorMessage';
import { useToast } from '@/hooks/useToast';
import { useCyclesQuery } from '../../../hooks/data/useCyclesQuery';
import { useAuth } from '@/contexts/AuthContext';
import type { CreateObjectiveModalProps } from '../objectives.types';
import StaticCombobox from '@/components/shared/StaticCombobox/StaticCombobox';

export default function CreateObjectiveModal({
  isOpen,
  onClose,
  onSubmit,
  editingObjective,
  defaultCycleId,
}: CreateObjectiveModalProps) {
  const toast = useToast();
  const { user } = useAuth();
  const { cycles, isLoading: cyclesLoading } = useCyclesQuery();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<ObjectiveFormData>({
    resolver: zodResolver(ObjectiveFormSchema),
    mode: 'onBlur',
    defaultValues: objectiveFormDefaults,
  });

  const selectedCycleId = watch('cycleId');

  // Filter active and planning cycles
  const availableCycles = useMemo(
    () => cycles?.filter((c) => c.status === 'active' || c.status === 'planning') || [],
    [cycles]
  );

  // Convert cycles to combobox options
  const cycleOptions = useMemo(
    () =>
      availableCycles.map((cycle) => ({
        value: cycle.id,
        label: `${cycle.name} (${cycle.status === 'active' ? 'Ativo' : 'Planejamento'})`,
      })),
    [availableCycles]
  );

  // Level options
  const levelOptions = useMemo(
    () => [
      { value: '1', label: 'Organizacional (Nível 1)' },
      { value: '2', label: 'Área (Nível 2)' },
      { value: '3', label: 'Time (Nível 3)' },
      { value: '4', label: 'Individual (Nível 4)' },
    ],
    []
  );

  // Get selected cycle dates (memoized to avoid dependency issues)
  const defaultCycleDates = useMemo(() => {
    if (!defaultCycleId || !cycles) return null;
    const selectedCycle = cycles.find(c => c.id === defaultCycleId);
    return selectedCycle ? {
      startDate: selectedCycle.startDate.toDate(),
      endDate: selectedCycle.endDate.toDate()
    } : null;
  }, [defaultCycleId, cycles]);

  useEffect(() => {
    if (editingObjective) {
      reset({
        title: editingObjective.title,
        description: editingObjective.description || '',
        cycleId: editingObjective.cycleId,
        ownerId: editingObjective.ownerId,
        level: editingObjective.level,
        parentId: editingObjective.parentObjectiveId || '',
        startDate: editingObjective.startDate.toDate(),
        endDate: editingObjective.endDate.toDate(),
      });
    } else {
      // Reset to defaults with optional pre-filled cycleId and dates
      reset({
        ...objectiveFormDefaults,
        ownerId: user?.uid || '',
        cycleId: defaultCycleId || '',
        startDate: defaultCycleDates?.startDate,
        endDate: defaultCycleDates?.endDate,
      });
    }
  }, [editingObjective, isOpen, reset, user, defaultCycleId, defaultCycleDates]);

  // Auto-populate dates from selected cycle
  useEffect(() => {
    if (selectedCycleId && cycles) {
      const selectedCycle = cycles.find(c => c.id === selectedCycleId);
      if (selectedCycle) {
        setValue('startDate', selectedCycle.startDate.toDate());
        setValue('endDate', selectedCycle.endDate.toDate());
      }
    }
  }, [selectedCycleId, cycles, setValue]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  /**
   * Convert date string or Date to local Date (avoiding timezone issues)
   * Input date="2025-01-15" should result in Jan 15 in local time, not UTC
   */
  const toLocalDate = (date: Date | string): Date => {
    if (date instanceof Date) return date;
    // Parse "YYYY-MM-DD" and create date in local timezone
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleFormSubmit = async (data: ObjectiveFormData) => {
    try {
      console.log('[CreateObjectiveModal] Submitting objective:', data);

      // Ensure dates are in local timezone
      const submitData = {
        ...data,
        startDate: toLocalDate(data.startDate),
        endDate: toLocalDate(data.endDate),
      };

      await onSubmit(submitData);
      toast.success(editingObjective ? 'Objetivo atualizado com sucesso!' : 'Objetivo criado com sucesso!');
      reset();
      onClose();
    } catch (error: any) {
      console.error('[CreateObjectiveModal] Error:', error);
      toast.error(`Erro ao ${editingObjective ? 'atualizar' : 'criar'} objetivo: ${error.message || 'Erro desconhecido'}`);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black bg-opacity-50 p-4">
      <div className="my-8 w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-montserrat text-2xl font-bold text-gray-900">
            {editingObjective ? 'Editar Objetivo' : 'Criar Novo Objetivo'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="space-y-4">
          {/* Cycle Selector - hide when in cycle context or when editing */}
          {!defaultCycleId && !editingObjective && (
            <div>
              <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
                Selecionar Ciclo <span className="text-red-500">*</span>
              </label>
              <Controller
                name="cycleId"
                control={control}
                render={({ field }) => (
                  <div className={clsx(errors.cycleId && 'rounded-lg border-2 border-red-500')}>
                    <StaticCombobox
                      options={cycleOptions}
                      selectedValue={field.value}
                      onChange={(value) => field.onChange(value || '')}
                      disabled={cyclesLoading}
                      placeholder={cyclesLoading ? 'Carregando ciclos...' : 'Selecione um ciclo'}
                    />
                  </div>
                )}
              />
              <ErrorMessage errors={errors} fieldPath="cycleId" />
              {!cyclesLoading && availableCycles.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">
                  Nenhum ciclo disponível. Crie um ciclo primeiro.
                </p>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
              Título <span className="text-red-500">*</span>
            </label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  className={clsx(
                    'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  )}
                  placeholder="Ex: Aumentar receita em 30%"
                />
              )}
            />
            <ErrorMessage errors={errors} fieldPath="title" />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
              Descrição
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  className={clsx(
                    'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  )}
                  rows={3}
                  placeholder="Descreva o objetivo..."
                />
              )}
            />
            <ErrorMessage errors={errors} fieldPath="description" />
          </div>

          {/* Owner (hidden, set to current user) */}
          <Controller
            name="ownerId"
            control={control}
            render={({ field }) => <input {...field} type="hidden" />}
          />

          {/* Level */}
          <div>
            <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
              Nível <span className="text-red-500">*</span>
            </label>
            <Controller
              name="level"
              control={control}
              render={({ field }) => (
                <div className={clsx(errors.level && 'rounded-lg border-2 border-red-500')}>
                  <StaticCombobox
                    options={levelOptions}
                    selectedValue={String(field.value)}
                    onChange={(value) => field.onChange(value ? Number(value) : 1)}
                    placeholder="Selecione o nível"
                  />
                </div>
              )}
            />
            <ErrorMessage errors={errors} fieldPath="level" />
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            {/* Start Date */}
            <div>
              <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
                Data de Início <span className="text-red-500">*</span>
              </label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    value={field.value ? new Date(field.value).toISOString().split('T')[0] : ''}
                    onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : null)}
                    className={clsx(
                      'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                      errors.startDate ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                )}
              />
              <ErrorMessage errors={errors} fieldPath="startDate" />
            </div>

            {/* End Date */}
            <div>
              <label className="mb-1 block font-montserrat text-sm font-medium text-gray-700">
                Data de Término <span className="text-red-500">*</span>
              </label>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="date"
                    value={field.value ? new Date(field.value).toISOString().split('T')[0] : ''}
                    onChange={(e) => field.onChange(e.target.value ? new Date(e.target.value) : null)}
                    className={clsx(
                      'w-full rounded-lg border px-4 py-2 font-montserrat focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200',
                      errors.endDate ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                )}
              />
              <ErrorMessage errors={errors} fieldPath="endDate" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border border-gray-300 px-6 py-2 font-montserrat font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-6 py-2 font-montserrat font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando...' : editingObjective ? 'Salvar Alterações' : 'Criar Objetivo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
